import type { Conversation, DeskDetail } from '@deskworks/shared-types';
import { useNavigate } from '@tanstack/react-router';
import { Bot, Crown, PenLine } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { EmptyState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useAgents, useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { agentRuntimeLabel } from '@/lib/labels';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

/** One-line explanation of what an agent can do in a conversation. */
export const CONVERSATION_RULES = t(
  'The agent runs in its own container. In a conversation it reads — the Desk, Work Items and connected systems. Where the desk allows it, switch on “Agent may write” and it changes things right in the chat; calls that need your OK wait here. Bigger work happens through Work Items.',
);

/**
 * "Agent may write" for one chat (docs/features/conversations.md#writing-in-a-chat). Shown where the desk lets chats
 * write; the agent hears about the switch with its next turn, and Deskworks checks it at every call.
 */
export function ChatWritesSwitch({ conv }: { conv: Conversation }) {
  const toggle = useApiMutation((writes: boolean) => api.conversations.update(conv.id, { writes }), {
    invalidate: [qk.conversation(conv.id)],
    success: (c) => (c.writes ? t('The agent may write in this chat') : t('The agent only reads in this chat')),
  });
  if (!conv.canWrite || conv.status !== 'active') return null;
  const checked = toggle.isPending ? !!toggle.variables : conv.writes;
  return (
    <label
      className={cn(
        'inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
        checked ? 'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-200' : 'text-muted-foreground',
      )}
      title={t("When on, the agent uses writing tools of the desk's connections right in this chat. Calls set to “ask” wait for your OK here.")}
    >
      <PenLine className="size-3.5" />
      {t('Agent may write')}
      <Switch checked={checked} onCheckedChange={(v) => toggle.mutate(v)} disabled={toggle.isPending} className="ml-0.5" />
    </label>
  );
}

/** "Talk to agent": pick one of the desk's agents, optionally write the first message. */
export function StartConversationDialog({ desk, children }: { desk: DeskDetail; children: ReactNode }) {
  const navigate = useNavigate();
  const agents = useAgents(desk.workspaceId);
  const [open, setOpen] = useState(false);
  const deskAgents = desk.agents.filter((a) => a.enabled);
  const defaultAgent = deskAgents.find((a) => a.role === 'lead')?.agentId ?? deskAgents[0]?.agentId ?? '';
  const [agentId, setAgentId] = useState(defaultAgent);
  const [message, setMessage] = useState('');

  const onOpenChange = (o: boolean) => {
    if (o) {
      setAgentId(defaultAgent);
      setMessage('');
    }
    setOpen(o);
  };

  const create = useApiMutation(
    () => api.conversations.create(desk.id, { agentId, message: message.trim() || undefined }),
    {
      errorTitle: t('Couldn’t start the conversation'),
      invalidate: [qk.deskConversations(desk.id)],
      onSuccess: (conv) => {
        setOpen(false);
        void navigate({
          to: '/w/$workspaceId/desks/$deskId',
          params: { workspaceId: desk.workspaceId, deskId: desk.id },
          search: { tab: 'chat', chat: conv.id },
        });
      },
    },
  );

  const runtimeOf = (id: string) => {
    const a = agents.data?.find((x) => x.id === id);
    if (!a) return null;
    return agentRuntimeLabel(a, a.runtimeProfile);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (agentId) create.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('Talk to agent')}</DialogTitle>
            <DialogDescription>{CONVERSATION_RULES}</DialogDescription>
          </DialogHeader>
          {deskAgents.length === 0 ? (
            <EmptyState compact title={t('No Agents on this desk')} description={t('Add an Agent to the desk first.')} />
          ) : (
            <div className="grid gap-2" role="radiogroup" aria-label={t('Agent')}>
              <Label>{t('Who do you want to talk to?')}</Label>
              {deskAgents.map((a) => {
                const runtime = runtimeOf(a.agentId);
                return (
                  <button
                    key={a.agentId}
                    type="button"
                    role="radio"
                    aria-checked={agentId === a.agentId}
                    onClick={() => setAgentId(a.agentId)}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-muted/50',
                      agentId === a.agentId && 'border-primary bg-accent/60 ring-1 ring-primary/40 hover:bg-accent/60',
                    )}
                  >
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      {a.role === 'lead' ? <Crown className="size-4" /> : <Bot className="size-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">
                        {a.name}
                        {a.role === 'lead' && <span className="ml-2 text-xs font-normal text-muted-foreground">{t('Lead Agent')}</span>}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">{runtime ?? '…'}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="conv-first-message">
              {t('First message')} <span className="font-normal text-muted-foreground">{t('(optional)')}</span>
            </Label>
            <Textarea
              id="conv-first-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder={t('e.g. What’s on this desk today?')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSubmit(e);
              }}
            />
            <p className="text-xs text-muted-foreground">{t('With a first message, the agent starts right away.')}</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={create.isPending} disabled={!agentId}>
              {t('Start conversation')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
